import { createTheme } from '@mui/material/styles'

// Change this color to customize the primary color for titlebar and buttons
const PRIMARY_COLOR = '#00E57D' // Default Material-UI blue - change this to your desired color
const SECONDARY_COLOR = '#444444'
const theme = createTheme({
    palette: {
        primary: {
            main: PRIMARY_COLOR,
        },
        secondary: {
            main: PRIMARY_COLOR, // Login/Register buttons use secondary, so keep them green
        },
    },
    components: {
        MuiLink: {
            styleOverrides: {
                root: {
                    color: SECONDARY_COLOR,
                    '&:visited': {
                        color: SECONDARY_COLOR,
                    },
                    '&:hover': {
                        color: '#555555',
                        opacity: 0.8,
                    },
                    // Ensure icons inside links use the same color
                    '& .MuiSvgIcon-root': {
                        color: SECONDARY_COLOR,
                        fill: SECONDARY_COLOR,
                    },
                    '& svg': {
                        color: SECONDARY_COLOR,
                        fill: SECONDARY_COLOR,
                    },
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    // Ensure icons in sidebar list items use the correct color
                    '& .MuiSvgIcon-root': {
                        color: SECONDARY_COLOR,
                        fill: SECONDARY_COLOR,
                    },
                    '& svg': {
                        color: SECONDARY_COLOR,
                        fill: SECONDARY_COLOR,
                    },
                    '& a': {
                        color: SECONDARY_COLOR,
                        '& .MuiSvgIcon-root': {
                            color: SECONDARY_COLOR,
                            fill: SECONDARY_COLOR,
                        },
                        '& svg': {
                            color: SECONDARY_COLOR,
                            fill: SECONDARY_COLOR,
                        },
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    // Default button text and icon color (for buttons without color prop)
                    // This applies to text buttons (default variant) without color prop
                    '&:not(.MuiButton-containedPrimary):not(.MuiButton-containedSecondary):not(.MuiButton-outlinedPrimary):not(.MuiButton-outlinedSecondary):not(.MuiButton-textPrimary):not(.MuiButton-textSecondary)': {
                        color: SECONDARY_COLOR,
                        '& .MuiSvgIcon-root': {
                            color: SECONDARY_COLOR+' !important',
                            fill: SECONDARY_COLOR+' !important',
                        },
                        '& svg': {
                            color: SECONDARY_COLOR + ' !important',
                            fill: SECONDARY_COLOR + ' !important',
                        },
                        '& .MuiTypography-root': {
                            color: SECONDARY_COLOR,
                        },
                    },
                    // Override primary buttons to be grey (except login/register which use secondary)
                    '&.MuiButton-containedPrimary, &.MuiButton-outlinedPrimary, &.MuiButton-textPrimary': {
                        color: SECONDARY_COLOR,
                        '& .MuiSvgIcon-root': {
                            color: SECONDARY_COLOR + ' !important',
                            fill: SECONDARY_COLOR + ' !important',
                        },
                        '& svg': {
                            color: SECONDARY_COLOR + ' !important',
                            fill: SECONDARY_COLOR + ' !important',
                        },
                        '& .MuiTypography-root': {
                            color: SECONDARY_COLOR,
                        },
                    },
                    // Tab button style for Flow/Data/Settings/etc. buttons
                    // Use className="tab-button" to apply this style
                    '&.tab-button': {
                        float: 'left',
                        backgroundColor: '#DDDDDD',
                        minWidth: 130,
                        margin: 1,
                        color: SECONDARY_COLOR,
                        '& .MuiSvgIcon-root': {
                            color: SECONDARY_COLOR,
                        },
                        '& .MuiTypography-root': {
                            color: SECONDARY_COLOR,
                        },
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    // Default icon button color
                    color: SECONDARY_COLOR,
                    // Override inherit color in AppBar/Toolbar to use secondary color
                    '&.MuiIconButton-colorInherit': {
                        color: `${SECONDARY_COLOR} !important`,
                        '& .MuiSvgIcon-root': {
                            color: `${SECONDARY_COLOR} !important`,
                            fill: `${SECONDARY_COLOR} !important`,
                        },
                        '& svg': {
                            color: `${SECONDARY_COLOR} !important`,
                            fill: `${SECONDARY_COLOR} !important`,
                        },
                    },
                    // Ensure icons inside icon buttons use the correct color
                    '& .MuiSvgIcon-root': {
                        color: SECONDARY_COLOR,
                    },
                    // Keep primary icon buttons green
                    '&.MuiIconButton-colorPrimary': {
                        color: PRIMARY_COLOR,
                        '& .MuiSvgIcon-root': {
                            color: PRIMARY_COLOR,
                        },
                    },
                    // Keep secondary icon buttons green (for login/register)
                    '&.MuiIconButton-colorSecondary': {
                        color: PRIMARY_COLOR,
                        '& .MuiSvgIcon-root': {
                            color: PRIMARY_COLOR,
                        },
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    // Remove default margins/padding to close gaps
                    margin: 0,
                    padding: 0,
                    boxShadow: 'none',
                    // Override icon button colors in AppBar to use secondary color instead of white
                    '& .MuiIconButton-root:not(.MuiIconButton-colorPrimary):not(.MuiIconButton-colorSecondary)': {
                        color: `${SECONDARY_COLOR} !important`,
                        '& .MuiSvgIcon-root': {
                            color: `${SECONDARY_COLOR} !important`,
                            fill: `${SECONDARY_COLOR} !important`,
                        },
                        '& svg': {
                            color: `${SECONDARY_COLOR} !important`,
                            fill: `${SECONDARY_COLOR} !important`,
                        },
                    },
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    // Remove default padding to close gaps
                    paddingLeft: '8px !important',
                    paddingRight: '8px !important',
                    minHeight: '48px !important',
                    // Override icon button colors in Toolbar to use secondary color instead of white
                    '& .MuiIconButton-root:not(.MuiIconButton-colorPrimary):not(.MuiIconButton-colorSecondary)': {
                        color: `${SECONDARY_COLOR} !important`,
                        '& .MuiSvgIcon-root': {
                            color: `${SECONDARY_COLOR} !important`,
                            fill: `${SECONDARY_COLOR} !important`,
                        },
                        '& svg': {
                            color: `${SECONDARY_COLOR} !important`,
                            fill: `${SECONDARY_COLOR} !important`,
                        },
                    },
                },
            },
        },
    },
})

export default theme

